
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchSubscriptions, createSubscription, processPayments, exportSubscriptions } from "@/api/subscriptions";
import { toast } from "@/hooks/use-toast";
import { Subscription } from "@/types/subscription";
import { useAuth } from "@/context/AuthContext";

export function useSubscriptionData() {
  const queryClient = useQueryClient();
  const { user, isAdmin } = useAuth();

  // We conditionally fetch subscriptions data based on admin status
  const { data: subscriptions, isLoading, error, isError } = useQuery({
    queryKey: ["subscriptions"],
    queryFn: async () => {
      console.log("Fetching subscriptions from Supabase...");
      try {
        const result = await fetchSubscriptions();
        console.log("Fetch result:", result);
        return result;
      } catch (err) {
        console.error("Error in fetchSubscriptions:", err);
        throw err;
      }
    },
    retry: 1,
    refetchOnMount: true,
    // Only fetch if user is admin
    enabled: !!user && isAdmin
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<Subscription, "id" | "totalPaid">) => createSubscription(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
    },
    onError: (error) => {
      console.error("Error creating subscription:", error);
      toast({
        title: "Error",
        description: "Failed to create subscription. Please try again.",
        variant: "destructive"
      });
    }
  });

  const paymentMutation = useMutation({
    mutationFn: processPayments,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
      toast({
        title: "Payments Processed",
        description: "Weekly payments have been processed successfully."
      });
    },
    onError: (error) => {
      console.error("Error processing payments:", error);
      toast({
        title: "Error",
        description: "Failed to process payments. Please try again.",
        variant: "destructive"
      });
    }
  });

  const exportMutation = useMutation({
    mutationFn: exportSubscriptions,
    onSuccess: (data) => {
      console.log("Export successful, creating download link");
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'subscriptions.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({
        title: "Export Successful",
        description: "Your subscriptions data has been downloaded as a CSV file."
      });
    },
    onError: (error) => {
      console.error("Error exporting subscriptions:", error);
      toast({
        title: "Error",
        description: "Failed to export subscriptions. Please try again.",
        variant: "destructive"
      });
    }
  });

  return {
    subscriptions,
    isLoading,
    error,
    isError,
    createSubscription: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    processPayments: paymentMutation.mutate,
    isProcessingPayments: paymentMutation.isPending,
    exportSubscriptions: exportMutation.mutate,
    isExporting: exportMutation.isPending
  };
}
